import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { SolicitudCamService } from '../../datosbasicos/solicitud-cam.service';
import { EnumCodigosTwTcAcciones } from 'src/app/components/_importacion/solicitudes/types/enum';
import { Observable, catchError, of, tap } from 'rxjs';
import { TwSolicitudCamValidationViewModel, ValidacionAceptacionSolicitudViewModel } from '../../../types/solicitudcam-datosbasicos.types';
import { EnumIdsTcEstadoSolicitudCam, EnumIdsTwTcEstadoAceptacionSolicitudCam, EnumIdsTwTcEstadoValidacionSolicitudCam } from '../../../types/enum';
import { NavMenuService } from 'src/app/components/core/components/navmenu/navmenu.service';

@Component({
    selector: 'app-validacion-aceptacion-wrapper',
    templateUrl: './validacion-aceptacion-wrapper.component.html',
    styleUrls: ['./validacion-aceptacion-wrapper.component.scss'],
})
export class ValidacionAceptacionWrapperComponent extends ComponentBase implements OnInit {
    solicitudCam: TwSolicitudCamValidationViewModel;
    visibilitySpinnerSave = false;
    validacionAllowed = false;
    aceptacionAllowed = false;
    formGroup: FormGroup;

    validacionAceptacionSolicitudVM: ValidacionAceptacionSolicitudViewModel;

    private listUrl = '/campanias/solicitudes';

    private activatedRoute$ = this.activatedRoute.paramMap.subscribe(params => {
        const idSolicitudCam: string | null = params.get('id');
        if (idSolicitudCam) {
            this.getSolicitudCam(parseInt(idSolicitudCam));
        }
    });

    private successMessageValidacion = 'Validación guardada satisfactoriamente';
    private successMessageAceptacion = 'Aceptación guardada satisfactoriamente';

    private aceptacionKey = 'aceptacion';
    private validacionKey = 'validacion';

    constructor(
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private solicitudCamService: SolicitudCamService,
        private navMenuService: NavMenuService,
        private router: Router,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
        protected notificacionService: NotificationService,
    ) {
        super(authService, notificacionService, exceptionHandler);
    }

    ngOnInit(): void {
        this.buildFormBase();
    }

    private buildFormBase(): void {
        // @autor: scalero1 - Revisar validaciones dinámicas en el componente validacion-aceptacion-form.
        this.formGroup = this.fb.group({
            validacion: this.fb.group({
                observacionesValidacion: [{ value: '', disabled: true }],
                fechaValidacion: [{ value: '', disabled: true }],
                usuarioValidacion: [{ value: '', disabled: true }],
                estadoValidacion: [{ value: EnumIdsTwTcEstadoValidacionSolicitudCam.PENDIENTE, disabled: true }, Validators.required],
            }),
            aceptacion: this.fb.group({
                fechaAceptacion: [{ value: '', disabled: true }],
                usuarioAceptacion: [{ value: '', disabled: true }],
            }),
        });

        this.form = this.formGroup;
    }

    /**
     * Una vez tenemos sabemos los permisos del usuario, reconstruimos el formulario,
     * añadiendo más controles, sus validaciones y si tienen que estar deshabilitados o no
     */
    private expandForm(): void {
        if (!this.validacionFormGroup || !this.aceptacionFormGroup) {
            return;
        }

        const validacionValidada = this.solicitudCam.IdEstadoSolicitudCam === EnumIdsTcEstadoSolicitudCam.ID_VALIDADA;

        if (this.validacionAllowed && !validacionValidada) {
            this.validacionFormGroup.controls['estadoValidacion'].enable();
            this.validacionFormGroup.controls['observacionesValidacion'].enable();
        } else {
            this.validacionFormGroup.controls['estadoValidacion'].disable();
            this.validacionFormGroup.controls['observacionesValidacion'].disable();
        }

        if (!this.isCA) {
            this.aceptacionFormGroup.addControl(
                'estadoAceptacion',
                new FormControl({ value: EnumIdsTwTcEstadoAceptacionSolicitudCam.PENDIENTE, disabled: true }),
            );
            this.aceptacionFormGroup.addControl('observacionesAceptacion', new FormControl({ value: '', disabled: true }));
        } else {
            this.aceptacionFormGroup.addControl(
                'estadoAceptacion',
                new FormControl(
                    { value: EnumIdsTwTcEstadoAceptacionSolicitudCam.PENDIENTE, disabled: !this.aceptacionAllowed || !validacionValidada },
                    Validators.required,
                ),
            );
            this.aceptacionFormGroup.addControl(
                'observacionesAceptacion',
                new FormControl({ value: '', disabled: !this.aceptacionAllowed || !validacionValidada }),
            );
        }
    }

    /**
     * Este método obtiene los datos de la solicitud indicada por parámetro.
     * Una vez obtenida, establece el valor para la propiedad readonly a partir de la información del usuario y los permisos de edición.
     * @param idSolicitudCam number
     */
    private getSolicitudCam(idSolicitudCam: number): void {
        this.solicitudCamService
            .getSolicitudCamForValidation(idSolicitudCam)
            .pipe(
                tap((v: TwSolicitudCamValidationViewModel) => {
                    this.solicitudCam = v;
                    this.navMenuService.updateMenuServiceForSolicitudCam(v.Id!, v.IdEstadoSolicitudCam!, v.IdTipoSolicitudCam);
                    this.validacionAllowed = this.checkValidacionPermissions();
                    this.aceptacionAllowed = this.checkAceptacionPermissions();

                    this.expandForm();

                    if (!this.esPeriodoVigente && this.solicitudCam.PeriodoPresentacionVigente === false) {
                        this.form.disable();
                        return;
                    }
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    onBack(): void {
        this.router.navigate([this.listUrl]);
    }

    onClickSave(): void {
        this.save(this.solicitudCam.Id!, this.solicitudCam.IdTipoSolicitudCam);
    }

    private save(idSolicitudCam: number, idTipoSolicitudCam: number): void {
        const payload = this.getPayloadForValidarAceptar(idSolicitudCam, idTipoSolicitudCam);
        this.waitForValidarAceptar(payload, idSolicitudCam);
    }

    /**
     * A partir de los datos del formulario y en función del rol del usuario y el estado de aceptación, construimos
     * el payload que será enviado a los métodos validar o aceptar en el core.
     * @param idSolicitudCam number
     * @param idTipoSolicitudCam number
     * @returns payload Datos a enviar para validar o aceptar
     */
    private getPayloadForValidarAceptar(idSolicitudCam: number, idTipoSolicitudCam: number): ValidacionAceptacionSolicitudViewModel {
        const payload = new ValidacionAceptacionSolicitudViewModel();
        payload.IdSolicitudCam = idSolicitudCam;
        payload.IdTipoSolicitudCam = idTipoSolicitudCam;
        payload.ObservacionesValidacion = this.validacionFormGroup.value.observacionesValidacion;
        payload.ObservacionesAceptacion = this.aceptacionFormGroup.value.observacionesAceptacion;

        if (this.isCA) {
            payload.IdEstadoValidacionEa = undefined;
            payload.IdEstadoValidacionCa = this.validacionFormGroup.value.estadoValidacion || EnumIdsTwTcEstadoValidacionSolicitudCam.PENDIENTE;
        } else {
            // Aquí el usuario es EA
            payload.IdEstadoValidacionEa = this.validacionFormGroup.value.estadoValidacion || EnumIdsTwTcEstadoValidacionSolicitudCam.PENDIENTE;
            payload.IdEstadoValidacionCa = undefined;
        }

        if (this.aceptacionFormGroup.value.estadoAceptacion !== EnumIdsTwTcEstadoAceptacionSolicitudCam.PENDIENTE) {
            payload.IdEstadoAceptacion = this.aceptacionFormGroup.value.estadoAceptacion || EnumIdsTwTcEstadoAceptacionSolicitudCam.PENDIENTE;
        } else {
            payload.IdEstadoAceptacion = EnumIdsTwTcEstadoAceptacionSolicitudCam.PENDIENTE;
        }
        return payload;
    }

    /**
     * Este método invoca a los endpoints de validar o aceptar o ambos,
     * en función de si el FormGroup correpondiente ha sido modificado.
     * Construye una promesa para cada invocación y espera a la resolución de ambas,
     * para volver a cargar los datos de la solicitud.
     * @param payload ValidacionAceptacionSolicitudViewModel Objeto con los datos a enviar al core para validar o aceptar.
     * @param idSolicitudCam number
     */
    private waitForValidarAceptar(payload: ValidacionAceptacionSolicitudViewModel, idSolicitudCam: number) {
        const promiseValidacion = () => {
            return new Promise(resolve => {
                if (this.validacionFormGroup.dirty) {
                    this.validar(payload).subscribe(res => {
                        resolve(res ? this.validacionKey : '');
                    });
                } else {
                    resolve('');
                }
            });
        };

        const promiseAceptacion = () => {
            return new Promise(resolve => {
                if (this.aceptacionFormGroup.dirty) {
                    this.aceptar(payload).subscribe(res => {
                        resolve(res ? this.aceptacionKey : '');
                    });
                } else {
                    resolve('');
                }
            });
        };

        // Esperamos a que concluya la promesa del servicio de validación, antes de invocar al de aceptación (si el formulario de aceptación tiene cambios)
        promiseValidacion().then((res: any) => {
            if (res === this.validacionKey) {
                this.addSuccessMessage(this.successMessageValidacion);
            }
            promiseAceptacion().then((res: any) => {
                if (res === this.aceptacionKey) {
                    this.addSuccessMessage(this.successMessageAceptacion);
                }
                this.buildFormBase(); // Volvemos a construir de cero el formulario
                this.getSolicitudCam(idSolicitudCam); // Volvemos a cargar los datos de la solicitud de cam.
            });
        });
    }

    private validar(payload: ValidacionAceptacionSolicitudViewModel): Observable<any> {
        this.visibilitySpinnerSave = true;
        return this.solicitudCamService.validar(payload).pipe(
            tap(() => {
                this.validacionAllowed = false;
                this.visibilitySpinnerSave = false;
                return of(true);
            }),
            catchError(error => {
                this.validacionAllowed = false;
                this.visibilitySpinnerSave = false;
                this.catchServerError(error);
                return of(false);
            }),
        );
    }

    private aceptar(payload: ValidacionAceptacionSolicitudViewModel): Observable<any> {
        this.visibilitySpinnerSave = true;
        return this.solicitudCamService.aceptar(payload).pipe(
            tap(() => {
                this.aceptacionAllowed = false;
                this.visibilitySpinnerSave = false;
                return of(true);
            }),
            catchError(error => {
                this.aceptacionAllowed = false;
                this.visibilitySpinnerSave = false;
                this.catchServerError(error);
                return of(false);
            }),
        );
    }

    /**
     * Este componente no permitirá validar la solicitud cuando el usuario actual tenga los permisos necesarios.
     * Además el estado de la solicitud tendrá que ser uno de los permitidos para la validación.
     * @returns boolean Devolverá true cuando la validación esté permitida. False cuando NO esté permitida.
     */
    private checkValidacionPermissions(): boolean {
        const puedeValidar = this.loggedInUser?.getAction(EnumCodigosTwTcAcciones.VALIDATE_SOL_CAM, this.solicitudCam?.IdEstadoSolicitudCam)?.Editable || false;
        return puedeValidar;
    }

    /**
     * Este componente no permitirá aceptar la solicitud cuando el usuario actual tenga los permisos necesarios.
     * Además el estado de la solicitud tendrá que ser uno de los permitidos para la aceptación.
     * @returns boolean Devolverá true cuando la aceptación esté permitida. False cuando NO esté permitida.
     */
    private checkAceptacionPermissions(): boolean {
        const puedeAceptar = this.loggedInUser?.getAction(EnumCodigosTwTcAcciones.ACCEPT_SOL_CAM, this.solicitudCam?.IdEstadoSolicitudCam)?.Editable || false;
        return puedeAceptar;
    }

    /**
     * Este componente no permitirá aceptar la solicitud cuando el usuario actual no tenga los permisos necesarios.
     * Además el estado de la solicitud tendrá que ser uno de los permitidos para la aceptación.
     * @returns boolean Devolverá true cuando la aceptación esté permitida. False cuando NO esté permitida.
     */
    private get formInvalid(): boolean {
        return !this.formGroup?.valid || !this.formGroup?.dirty || this.formGroup?.pristine;
    }

    get disableSave(): boolean {
        return this.formInvalid || (!this.validacionAllowed && !this.aceptacionAllowed) || this.visibilitySpinnerSave || !this.esPeriodoVigente;
    }

    get readonly(): boolean {
        return !this.validacionAllowed && !this.aceptacionAllowed;
    }

    get validacionFormGroup(): FormGroup {
        return this.form?.get(this.validacionKey) as FormGroup;
    }

    get aceptacionFormGroup(): FormGroup {
        return this.form?.get(this.aceptacionKey) as FormGroup;
    }

    get esPeriodoVigente(): boolean {
        return this.solicitudCam?.PeriodoVigente;
    }
}
